import React, { useEffect, useRef, useState } from "react";
import { CheckboxField, CheckboxFieldProps, Icon, Label, Spacer, Text, Tooltip } from "../dm";
import { Box } from "../dm/layout";
import { useUid } from "../../utils";
import * as styles from "./checkbox-group.module.scss";

interface CheckboxGroupProps {
  name: string;
  labelGroup: string;
  required?: boolean;
  invalid?: boolean;
  errorMessage?: React.ReactNode;
  options: CheckboxOption[];
  onChange?: (value: string) => void;
  layout?: "horizontal" | "vertical";
  selectedCheckboxValues?: string;
  singleSelect?: boolean;
}

export interface CheckboxOption extends CheckboxFieldProps {
  label: string;
  value: string;
  tooltip?: React.ReactNode;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  labelGroup,
  options,
  required,
  invalid,
  errorMessage,
  onChange,
  layout = "vertical",
  selectedCheckboxValues = "",
  singleSelect = false,
  ...otherProps
}) => {
  const [selectedValues, setSelectedValues] = useState<string>(selectedCheckboxValues || "");

  useEffect(() => {
    setSelectedValues(selectedCheckboxValues || "");
  }, [selectedCheckboxValues]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onChangeRef.current?.(selectedValues);
  }, [selectedValues]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    let updatedValues: string;

    if (singleSelect) {
      updatedValues = selectedValues === value ? "" : value;
    } else {
      updatedValues = selectedValues.includes(value)
        ? selectedValues
            .split(",")
            .filter((v) => v !== value)
            .join(",")
        : selectedValues
          ? `${selectedValues},${value}`
          : value;
    }

    setSelectedValues(updatedValues);
    onChange?.(updatedValues);
  };

  const uid = useUid();
  const controlId = `checkbox-group-${uid}`;

  return (
    <Box>
      {labelGroup && (
        <Label htmlFor={controlId} invalid={invalid} required={required}>
          {labelGroup}
        </Label>
      )}
      <div
        className={`${styles.options} ${layout === "horizontal" ? styles.horizontal : styles.vertical}`}
        id={controlId}
      >
        {options.map((option) => {
          const { tooltip, ...checkboxOption } = option;
          return (
            <div key={option.value} className={styles.optionWrapper}>
              <CheckboxField
                {...otherProps}
                {...checkboxOption}
                checked={singleSelect ? selectedValues === option.value : selectedValues.includes(option.value)}
                invalid={invalid}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(event)}
              />
              {tooltip && (
                <div className={styles.tooltipTrigger}>
                  <Tooltip placement="top" text={tooltip}>
                    <Icon name="info" size="m" />
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {errorMessage && invalid && (
        <>
          <Spacer size="xxxs" />
          <Text kind="error" size="small">
            {errorMessage}
          </Text>
        </>
      )}
    </Box>
  );
};
